import {Alert} from '@wandb/weave/components/Alert';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  constString,
  isAssignableTo,
  list,
  listObjectType,
  maybe,
  opArtifactVersionName,
  opArtifactVersionUsedBy,
  opJoinToStr,
  opPick,
  opProjectLink,
  opRunCreatedAt,
  opRunJobType,
  opRunLink,
  opRunLoggedArtifactVersions,
  opRunProject,
  opRunRuntime,
  opRunSummary,
  opRunUsedArtifactVersions,
  opRunUser,
  opUserLink,
  typedDict,
  typedDictPropertyTypes,
  union,
  varNode,
} from '@wandb/weave/core';
import {useNodeWithServerType} from '@wandb/weave/react';
import * as React from 'react';

import {QuickPanelWBReactTable} from '../../../components/Panel2/PanelWBReactTable';

const inputType = 'artifactVersion' as const;
type ConfigType = {};

export const PanelArtifactVersionConsumptionTable: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType>
> = props => {
  const usageRunsNode = React.useMemo(
    () =>
      opArtifactVersionUsedBy({
        artifactVersion: props.input,
      }),
    [props.input]
  );

  const usageRunsNodeSummaryNode = React.useMemo(
    () => opRunSummary({run: usageRunsNode}),
    [usageRunsNode]
  );
  const usageRunsNodeSummaryNodeType = useNodeWithServerType(
    usageRunsNodeSummaryNode
  );

  const downCols = React.useMemo(() => {
    let cols = downstreamTableArtifactColumns();
    if (
      !usageRunsNodeSummaryNodeType.loading &&
      isAssignableTo(
        usageRunsNodeSummaryNodeType.result.type,
        list(typedDict({}))
      )
    ) {
      cols = [
        cols[0],
        ...Object.entries(
          typedDictPropertyTypes(
            listObjectType(usageRunsNodeSummaryNodeType.result.type)
          )
        ).reduce(
          (acc, a) => {
            if (
              !a[0].startsWith('_') &&
              isAssignableTo(a[1], maybe(union(['number'])))
            ) {
              acc.push({
                name: `s.${a[0]}`,
                selectFn: opPick({
                  obj: opRunSummary({run: varNode('run', 'row')}),
                  key: constString(a[0]),
                }),
              });
            }
            return acc;
          },
          [] as typeof cols
        ),
        ...cols.slice(1),
      ];
    }
    return cols;
  }, [
    usageRunsNodeSummaryNodeType.loading,
    usageRunsNodeSummaryNodeType.result,
  ]);

  return (
    <div style={{height: '100%'}}>
      <QuickPanelWBReactTable
        input={usageRunsNode as any}
        columns={downCols}
        emptyContent={
          <Alert severity="info">This artifact is not used by any runs.</Alert>
        }
      />
    </div>
  );
};

function downstreamTableArtifactColumns() {
  const rowType = 'run';
  return [
    {
      name: 'Run',
      selectFn: opRunLink({
        run: varNode(rowType, 'row'),
      }),
    },
    {
      name: 'Job Type',
      selectFn: opRunJobType({
        run: varNode(rowType, 'row'),
      }),
    },
    {
      name: 'Project',
      selectFn: opProjectLink({
        project: opRunProject({
          run: varNode(rowType, 'row'),
        }),
      }),
    },
    {
      name: 'User',
      selectFn: opUserLink({
        user: opRunUser({
          run: varNode(rowType, 'row'),
        }),
      }),
    },
    {
      name: 'Used Artifacts',
      selectFn: opJoinToStr({
        arr: opArtifactVersionName({
          artifactVersion: opRunUsedArtifactVersions({
            run: varNode(rowType, 'row'),
          }),
        }),
        sep: constString(', '),
      }),
    },
    {
      name: 'Logged Artifacts',
      selectFn: opJoinToStr({
        arr: opArtifactVersionName({
          artifactVersion: opRunLoggedArtifactVersions({
            run: varNode(rowType, 'row'),
          }),
        }),
        sep: constString(', '),
      }),
    },

    {
      name: 'Ran On',
      selectFn: opRunCreatedAt({
        run: varNode(rowType, 'row'),
      }),
    },
    {
      name: 'Duration (sec)',
      selectFn: opRunRuntime({
        run: varNode(rowType, 'row'),
      }),
    },
  ];
}
